/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { MapRef } from 'react-map-gl/mapbox'

import { SavedViewPopover } from '@/components/ui/saved-views/SavedViewPopover'
import { useSavedViews } from '@/components/ui/saved-views/useSavedViews'
import { useSettledDirty } from '@/components/ui/saved-views/useSettledDirty'
import { getCenterFromBounds, getResponsiveUSZoom, getUSBounds } from '@/lib/map/geo'

import { useTrackerMapContext, type TrackerSavedView } from './TrackerMapContext'

/**
 * What a saved view is compared on. The viewport and pane sizes are left out, so panning the map or
 * dragging a divider doesn't count as a change; saving still stores them.
 */
export const comparable = (view: Omit<TrackerSavedView, 'name'>) => ({
  companyId: view.companyId ?? null,
  filters: view.filters,
  layoutMode: view.sidebarState.layoutMode,
  limitToMap: view.sidebarState.limitToMap,
  style: view.mapState.style,
  clustersEnabled: view.mapState.clustersEnabled,
  trackerLabels: view.mapState.trackerLabels,
  showQuickView: view.mapState.showQuickView,
  columns: view.columns,
})

/** The tracker map's saved views, in the profile's savedViews. */
export const TrackerSavedViews = ({ mapRef }: { mapRef: React.RefObject<MapRef | null> }) => {
  const context = useTrackerMapContext()
  const { selectedView, setSelectedView } = context
  const saved = useSavedViews<TrackerSavedView>('trackers')

  const isDirty = useSettledDirty(
    selectedView,
    comparable(context.currentView('', false)),
    context.isSwitchingView || context.isLoading
  )

  const store = (view: TrackerSavedView) => {
    saved.store(view)
    setSelectedView(view)
  }

  const handleReset = () => {
    context.reset()
    const map = mapRef.current?.getMap()
    if (map) {
      const center = getCenterFromBounds(getUSBounds(6))
      map.flyTo({ center: [center.longitude, center.latitude], zoom: getResponsiveUSZoom(), duration: 1000 })
    }
  }

  return (
    <SavedViewPopover
      selected={selectedView}
      viewNames={saved.viewNames}
      isDirty={isDirty}
      onSaveAsNew={(name, isDefault) =>
        saved.storeNew(name, (trimmedName) => {
          const view = context.currentView(trimmedName, isDefault)
          setSelectedView(view)
          return view
        })
      }
      onSaveSelected={() => selectedView && store(context.currentView(selectedView.name, !!selectedView.isDefault))}
      onSetDefault={(isDefault) => selectedView && store({ ...selectedView, isDefault })}
      onDelete={() => {
        if (!selectedView) return
        saved.remove(selectedView.name)
        setSelectedView(null)
      }}
      onSelect={(name) => {
        const view = saved.byName[name]
        if (view) context.applyView(view)
      }}
      onReset={handleReset}
      resetHelp="Clears the search and all filters, puts the layout, columns and map settings back to how they start, and zooms out. Keeps the company and your saved views."
    />
  )
}
